import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import baseUrl from '../../../utils/baseUrl';
import getToken from '../../../utils/getToken';

type ReviewableType = 'TradingPhotoCard' | 'MatchedTradingPhotoCard';

export const useTradeReviewPageQuery = () => {
  const [searchParams] = useSearchParams();

  const reviewId = searchParams.get('reviewId');
  const reviewableId = searchParams.get('reviewableId');
  const reviewableType = searchParams.get('reviewableType') as ReviewableType | null;

  const isReviewMode = !!reviewId;

  const {
    data: reviewData,
    isLoading: isReviewLoading,
    isError: isReviewError
  } = useQuery({
    queryKey: ['review', reviewId],
    enabled: isReviewMode,
    queryFn: async () => {
      const response = await fetch(`${baseUrl()}/api/v1/reviews/${reviewId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        }
      });
      if (!response.ok) throw new Error('리뷰 조회 실패');
      return response.json();
    }
  });

  const {
    data: cardData,
    isLoading: isCardLoading,
    isError: isCardError
  } = useQuery({
    queryKey: ['photoCard', reviewableId, reviewableType],
    enabled: !isReviewMode && !!reviewableId && !!reviewableType,
    queryFn: async () => {
      const response = await fetch(getPhotoCardEndpoint(reviewableId!, reviewableType!), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        }
      });
      const data = await response.json();
      return reviewableType === 'TradingPhotoCard'
        ? data.tradingPhotoCard
        : data.matchedTradingPhotoCard;
    }
  });

  if (isReviewError || isCardError) {
    throw new Error('Failed to fetch review or card data');
  }

  return {
    mode: isReviewMode ? 'review' : 'request',
    data: isReviewMode ? reviewData : cardData,
    isLoading: isReviewMode ? isReviewLoading : isCardLoading
  };
};

const getPhotoCardEndpoint = (id: string, type: ReviewableType): string => {
  if (type === 'TradingPhotoCard') {
    return `${baseUrl()}/api/v1/trading_photo_cards/${id}`;
  }
  if (type === 'MatchedTradingPhotoCard') {
    return `${baseUrl()}/api/v1/matched_trading_photo_cards/${id}`;
  }
  throw new Error(`Unknown reviewableType: ${type}`);
};